import { useEffect, useState, useRef, useCallback } from "react"
import type { DiscoverFiltersProps, DiscoverFilters as DiscoverFiltersType } from "~/react/types/discover"
import {
  discovers_path,
  discover_views_path,
  discover_view_path,
} from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import ProactiveAiPrompt from "~/react/components/proactive-ai-prompt"
import { Button } from "~/react/ui/button"
import { ToggleButton } from "~/react/ui/toggle-button"
import { Icon } from "~/react/ui/icon"
import { CompanyNameFilter } from "~/react/components/discover_filters/company_name_filter"
import { CompanySizeFilter } from "~/react/components/discover_filters/company_size_filter"
import { IndustryFilter } from "~/react/components/discover_filters/industry_filter"
import { CompanyTypeFilter } from "~/react/components/discover_filters/company_type_filter"
import { KeywordsFilter } from "~/react/components/discover_filters/keywords_filter"
import { FoundedYearFilter } from "~/react/components/discover_filters/founded_year_filter"
import { LocationFilter } from "~/react/components/discover_filters/location_filter"
import { SimilarCompaniesFilter } from "~/react/components/discover_filters/similar_companies_filter"
import { TechnologiesFilter } from "~/react/components/discover_filters/technologies_filter"
import { FundingsFilter } from "~/react/components/discover_filters/fundings_filter"
import { DiscoverCompactFilter } from "~/react/components/discover_filters/compact_filters_summary"

const t = (key: string) => window.i18n.t(key)

// Helper to ensure value is always an array (handles single string or object from saved searches)
// Flattens tuples to codes - labels are passed separately via locationLabels prop
function ensureArray(value: unknown): string[] {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) {
    return value.map((item) => {
      // Flatten tuples to just the code (first element)
      if (Array.isArray(item)) return String(item[0])
      return String(item)
    })
  }
  if (typeof value === "object") return Object.keys(value)
  return [String(value)]
}

interface DiscoverFiltersComponentProps extends DiscoverFiltersProps {
  // Filter state lifted from parent
  filters: DiscoverFiltersType
  updateFilter: <K extends keyof DiscoverFiltersType>(
    key: K,
    value: DiscoverFiltersType[K]
  ) => void
  clearAllFilters: () => void
  hasFilters: boolean
  showInlinePrompt?: boolean
  onAiPromptSubmit?: (query: string) => void
  // Callback when filters change
  onFiltersChange: (filters: DiscoverFiltersType) => void
}

export default function DiscoverFilters({
  filterOptions,
  savedSearches = [],
  entitlement,
  viewId,
  filters,
  updateFilter,
  clearAllFilters,
  hasFilters,
  showInlinePrompt,
  onAiPromptSubmit,
  onFiltersChange,
}: DiscoverFiltersComponentProps) {
  const [searchName, setSearchName] = useState("")
  const [isCompactViewEnabled, setIsCompactViewEnabled] = useState(false)
  const [aiPromptPadding, setAiPromptPadding] = useState(0)
  const aiPromptRef = useRef<HTMLDivElement | null>(null)
  // Track if this is the initial mount to avoid re-applying existing filters
  const isInitialMount = useRef(true)
  // Track if we're doing a manual clear to skip auto-apply
  const isManualClear = useRef(false)

  // Get the current view name if editing an existing saved search
  const currentView = viewId ? savedSearches.find((s) => s.id === viewId) : null

  // Initialize search name from current view when component mounts or viewId changes
  useEffect(() => {
    if (currentView) {
      setSearchName(currentView.name)
    }
  }, [viewId, currentView?.name])

  // Show Clear All and Save buttons when there are filters OR when viewing a saved search
  const showActions = hasFilters || !!viewId

  // Reserve vertical space equal to the inline AI prompt height.
  // Recalculate whenever the prompt mounts/unmounts or its size changes.
  useEffect(() => {
    if (!showInlinePrompt || !aiPromptRef.current) {
      setAiPromptPadding(0)
      return
    }

    const updatePadding = () => {
      if (!aiPromptRef.current) return
      const promptNode = aiPromptRef.current.querySelector<HTMLElement>(
        ".proactive-ai-prompt",
      )
      if (!promptNode) return
      setAiPromptPadding(promptNode.offsetHeight + 24)
    }

    updatePadding()

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => updatePadding())
        : null

    if (resizeObserver && aiPromptRef.current) {
      const promptNode = aiPromptRef.current.querySelector<HTMLElement>(
        ".proactive-ai-prompt",
      )
      if (promptNode) resizeObserver.observe(promptNode)
    }

    window.addEventListener("resize", updatePadding)

    return () => {
      window.removeEventListener("resize", updatePadding)
      if (resizeObserver) resizeObserver.disconnect()
    }
  }, [showInlinePrompt])

  // Defensive fallback - filterOptions should always be provided by prepare_react_props
  // but this prevents runtime errors if something goes wrong during prop passing
  if (!filterOptions) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-muted">Loading filters...</p>
      </div>
    )
  }

  // Handle Clear All: clear local state and notify parent
  const handleClearAll = useCallback(() => {
    // Set flag to skip auto-apply effect
    isManualClear.current = true
    clearAllFilters()
    onFiltersChange({})
  }, [clearAllFilters, onFiltersChange])

  const handleApplyFilters = useCallback(() => {
    onFiltersChange(filters)
  }, [filters, onFiltersChange])

  // Auto-apply filters when they change (but not on initial mount or manual clear)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Skip auto-apply if we just did a manual clear (already navigated)
    if (isManualClear.current) {
      isManualClear.current = false
      return
    }

    const timer = setTimeout(() => {
      handleApplyFilters()
    }, 500) // Debounce 500ms

    return () => clearTimeout(timer)
  }, [handleApplyFilters])

  // Get filters as JSON string for saving (excludes empty values)
  const getFiltersJson = () => {
    const cleanFilters: Record<string, unknown> = {}
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (Array.isArray(value) && value.length === 0) return
      cleanFilters[key] = value
    })
    return JSON.stringify(cleanFilters)
  }

  return (
    <div className="collapsable-filters">
      <div className="collapsable-filters__header">
        <h2 className="collapsable-filters__title">
          {t("js.back.discover.title")}
        </h2>

        <div className="collapsable-filters__actions">
          {/* Clear All - shows when filters exist */}
          {hasFilters && (
            <>
              <ToggleButton
                type="button"
                variant="ghost"
                size="sm"
                iconOnly
                selected={isCompactViewEnabled}
                onSelectedChange={setIsCompactViewEnabled}
                aria-label={t("js.back.discover.compact.toggle_view")}
              >
                <Icon icon="arrows-to-dotted-line" aria-hidden="true" />
              </ToggleButton>
              <Button variant="default" size="sm" onClick={handleClearAll}>
                {t("js.back.discover.clear_all")}
              </Button>
            </>
          )}
        </div>
      </div>

      {showInlinePrompt && (
        <div ref={aiPromptRef} className="collapsable-filters__ai-prompt">
          <ProactiveAiPrompt isSmall onSubmit={onAiPromptSubmit} />
        </div>
      )}

      {isCompactViewEnabled ? (
        <div className="collapsable-filters__compact">
          <DiscoverCompactFilter
            filters={filters}
            filterOptions={filterOptions}
            onDisableCompactView={() => setIsCompactViewEnabled(false)}
          />
        </div>
      ) : (
        <div
          className="collapsable-filters__default"
          style={
            aiPromptPadding > 0
              ? { paddingBottom: `${aiPromptPadding}px` }
              : undefined
          }
        >
          <LocationFilter
            includedContinentValues={ensureArray(filters.location_continent_included)}
            excludedContinentValues={ensureArray(filters.location_continent_excluded)}
            includedBusinessRegionValues={ensureArray(filters.location_business_region_included)}
            excludedBusinessRegionValues={ensureArray(filters.location_business_region_excluded)}
            includedCountryValues={ensureArray(filters.location_country_included)}
            excludedCountryValues={ensureArray(filters.location_country_excluded)}
            includedStateValues={ensureArray(filters.location_state_included)}
            excludedStateValues={ensureArray(filters.location_state_excluded)}
            includedCityValues={ensureArray(filters.location_city_included)}
            excludedCityValues={ensureArray(filters.location_city_excluded)}
            onChangeIncludedContinent={(value) =>
              updateFilter("location_continent_included", value)
            }
            onChangeExcludedContinent={(value) =>
              updateFilter("location_continent_excluded", value)
            }
            onChangeIncludedBusinessRegion={(value) =>
              updateFilter("location_business_region_included", value)
            }
            onChangeExcludedBusinessRegion={(value) =>
              updateFilter("location_business_region_excluded", value)
            }
            onChangeIncludedCountry={(value) =>
              updateFilter("location_country_included", value)
            }
            onChangeExcludedCountry={(value) =>
              updateFilter("location_country_excluded", value)
            }
            onChangeIncludedState={(value) =>
              updateFilter("location_state_included", value)
            }
            onChangeExcludedState={(value) =>
              updateFilter("location_state_excluded", value)
            }
            onChangeIncludedCity={(value) =>
              updateFilter("location_city_included", value)
            }
            onChangeExcludedCity={(value) =>
              updateFilter("location_city_excluded", value)
            }
            countryNames={filterOptions.country_names}
            stateNames={filterOptions.state_names}
          />

          <IndustryFilter
            includedValues={ensureArray(filters.industry_included)}
            excludedValues={ensureArray(filters.industry_excluded)}
            industries={filterOptions.industries}
            onChangeIncluded={(value) =>
              updateFilter("industry_included", value)
            }
            onChangeExcluded={(value) =>
              updateFilter("industry_excluded", value)
            }
          />

          <KeywordsFilter
            includedValues={ensureArray(filters.keywords_included)}
            excludedValues={ensureArray(filters.keywords_excluded)}
            operator={(filters.keywords_operator as "and" | "or") || "and"}
            onChangeIncluded={(value) =>
              updateFilter("keywords_included", value)
            }
            onChangeExcluded={(value) =>
              updateFilter("keywords_excluded", value)
            }
            onChangeOperator={(value) =>
              updateFilter("keywords_operator", value)
            }
          />

          <CompanySizeFilter
            value={ensureArray(filters.company_size)}
            sizes={filterOptions.company_sizes}
            onChange={(value) => updateFilter("company_size", value)}
          />

          <CompanyNameFilter
            value={ensureArray(filters.domains)}
            onChange={(value) => updateFilter("domains", value)}
          />

          <SimilarCompaniesFilter
            value={filters.similar_to_domain}
            onChange={(value) => updateFilter("similar_to_domain", value)}
            isLocked={!entitlement.allowSimilarCompaniesFilter}
          />

          <CompanyTypeFilter
            includedValues={ensureArray(filters.company_type_included)}
            excludedValues={ensureArray(filters.company_type_excluded)}
            companyTypes={filterOptions.company_types}
            onChangeIncluded={(value) =>
              updateFilter("company_type_included", value)
            }
            onChangeExcluded={(value) =>
              updateFilter("company_type_excluded", value)
            }
          />

          <FoundedYearFilter
            fromValue={filters.founded_year_from}
            toValue={filters.founded_year_to}
            includedValues={ensureArray(filters.founded_year_included)}
            excludedValues={ensureArray(filters.founded_year_excluded)}
            onChangeFrom={(value) => updateFilter("founded_year_from", value)}
            onChangeTo={(value) => updateFilter("founded_year_to", value)}
            onChangeIncluded={(value) =>
              updateFilter("founded_year_included", value)
            }
            onChangeExcluded={(value) =>
              updateFilter("founded_year_excluded", value)
            }
            isLocked={!entitlement.allowFoundedYearFilter}
          />

          <TechnologiesFilter
            includedValues={ensureArray(filters.technology_included)}
            excludedValues={ensureArray(filters.technology_excluded)}
            operator={(filters.technology_operator as "and" | "or") || "and"}
            technologies={filterOptions.technologies || {}}
            onChangeIncluded={(value) =>
              updateFilter("technology_included", value)
            }
            onChangeExcluded={(value) =>
              updateFilter("technology_excluded", value)
            }
            onChangeOperator={(value) =>
              updateFilter("technology_operator", value)
            }
            isLocked={!entitlement.allowTechnologiesFilter}
          />

          <FundingsFilter
            seriesValues={ensureArray(filters.funding_series)}
            amountFrom={filters.funding_amount_from}
            amountTo={filters.funding_amount_to}
            dateValue={filters.funding_date}
            dateFrom={filters.funding_date_from}
            dateTo={filters.funding_date_to}
            onChangeSeriesValues={(value) =>
              updateFilter("funding_series", value)
            }
            onChangeAmountFrom={(value) =>
              updateFilter("funding_amount_from", value)
            }
            onChangeAmountTo={(value) =>
              updateFilter("funding_amount_to", value)
            }
            onChangeDateValue={(value) => updateFilter("funding_date", value)}
            onChangeDateFrom={(value) =>
              updateFilter("funding_date_from", value)
            }
            onChangeDateTo={(value) => updateFilter("funding_date_to", value)}
            isLocked={!entitlement.allowFundingsFilter}
          />
        </div>
      )}

      <div className="collapsable-filters__footer">
        {/* Save Search - shows when filters exist OR when viewing a saved search */}
        {showActions && (
          <div className="dropdown">
            <button
              type="button"
              className="h-button h-button--ghost h-button--sm"
              data-bs-toggle="dropdown"
              disabled={!hasFilters}
            >
              {t("js.back.discover.save")}
            </button>
            <div className="dropdown-menu pull-right">
              <form method="post" action={discover_views_path()}>
                <input
                  type="hidden"
                  name="authenticity_token"
                  value={getCsrfToken()}
                />
                {viewId && (
                  <input type="hidden" name="view[id]" value={viewId} />
                )}
                <input
                  type="hidden"
                  name="view[filters]"
                  value={getFiltersJson()}
                />

                <div className="m-4">
                  <label className="h-label" htmlFor="save-search-name">
                    {t("js.back.discover.save_search.name_label")}
                  </label>
                  <input
                    id="save-search-name"
                    type="text"
                    name="view[name]"
                    className="h-input h-input--sm h-input--block"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                  />
                </div>

                {viewId ? (
                  <>
                    <input
                      type="submit"
                      className="dropdown-menu__submit"
                      value={t("js.back.discover.save_search.create_new")}
                      name="create"
                      formAction={discover_views_path()}
                      formMethod="post"
                      data-loading="none"
                    />
                    <input
                      type="submit"
                      className="dropdown-menu__submit"
                      value={t("js.back.discover.save_search.update")}
                      name="update"
                      formAction={discover_view_path({ id: viewId })}
                      formMethod="put"
                      data-loading="none"
                    />
                  </>
                ) : (
                  <input
                    type="submit"
                    className="dropdown-menu__submit"
                    value={t("js.back.discover.save_search.save")}
                    name="create"
                  />
                )}
              </form>
            </div>
          </div>
        )}

        <div className="dropdown">
          <button
            type="button"
            className="h-button h-button--ghost h-button--sm"
            data-bs-toggle="dropdown"
          >
            {t("js.back.discover.load_search")}
          </button>
          {savedSearches.length === 0 ? (
            <div className="dropdown-menu pull-right">
              <div className="empty-space empty-space--sm p-4">
                <strong className="empty-space__title">
                  {t("js.back.discover.no_saved_searches.title")}
                </strong>
                <p className="empty-space__desc">
                  {t("js.back.discover.no_saved_searches.description")}
                </p>
              </div>
            </div>
          ) : (
            <div className="dropdown-menu main-actions__dropdown pull-right">
              <ul>
                {savedSearches.map((search) => (
                  <li key={search.id} className="dropdown-menu__item">
                    <a
                      className="dropdown-menu__action"
                      href={`${discovers_path()}?view_id=${search.id}`}
                    >
                      {search.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
